// URL encoding utilities to handle consistent parameter encoding/decoding
// Addresses the issue where URLs can have mixed encoding (+ vs %20, etc.)

/**
 * Safely decode a URL parameter value, handling both + and %20 encoding
 * @param {string} value - The URL parameter value to decode
 * @returns {string} Properly decoded value
 */
export const safeDecodeURIComponent = (value) => {
  if (!value) return value;
  
  try {
    // First decode any percent-encoded characters
    let decoded = decodeURIComponent(value);
    
    // Then handle + signs that might represent spaces (form-style encoding)
    // Note: decodeURIComponent doesn't handle + to space conversion
    decoded = decoded.replace(/\+/g, ' ');
    
    return decoded;
  } catch (error) {
    console.warn('Failed to decode URL parameter:', value, error);
    // Fallback: just replace + with spaces if decoding fails
    return value.replace(/\+/g, ' ');
  }
};

/**
 * Safely encode a URL parameter value for use in query strings
 * Always uses proper percent encoding (%20 for spaces, %26 for &, etc.)
 * @param {string} value - The value to encode
 * @returns {string} Properly encoded value
 */
export const safeEncodeURIComponent = (value) => {
  if (!value) return value;
  
  try {
    return encodeURIComponent(value);
  } catch (error) {
    console.warn('Failed to encode URL parameter:', value, error);
    return value;
  }
};

/**
 * Get a URL search parameter with proper decoding
 * Handles both + and %20 encoding consistently
 * @param {URLSearchParams} searchParams - URLSearchParams instance
 * @param {string} key - Parameter key to get
 * @returns {string|null} Decoded parameter value or null if not found
 */
export const getSearchParam = (searchParams, key) => {
  const rawValue = searchParams.get(key);
  if (!rawValue) return null;
  
  // URLSearchParams.get() already handles most decoding, but we need to
  // ensure consistency for edge cases and handle + encoding if present
  return safeDecodeURIComponent(rawValue);
};

/**
 * Set a URL search parameter with proper encoding
 * @param {URLSearchParams} searchParams - URLSearchParams instance
 * @param {string} key - Parameter key to set
 * @param {string} value - Parameter value to set
 */
export const setSearchParam = (searchParams, key, value) => {
  if (!value) {
    searchParams.delete(key);
    return;
  }
  
  // Use native URLSearchParams.set() which handles encoding properly
  // No need to pre-encode since URLSearchParams handles it
  searchParams.set(key, value);
};

/**
 * Build a query string from parameters with consistent encoding
 * @param {Object} params - Object with key-value pairs
 * @returns {string} Properly formatted query string (without leading ?)
 */
export const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      setSearchParam(searchParams, key, value);
    }
  });
  
  return searchParams.toString();
};

/**
 * Parse a query string into an object with proper decoding
 * @param {string} queryString - Query string (with or without leading ?)
 * @returns {Object} Object with decoded key-value pairs
 */
export const parseQueryString = (queryString) => {
  const searchParams = new URLSearchParams(queryString);
  const result = {};
  
  for (const [key] of searchParams.entries()) {
    result[key] = getSearchParam(searchParams, key);
  }
  
  return result;
};

/**
 * Normalize a category name from URL parameters
 * Ensures consistent handling regardless of how it was encoded in the URL
 * @param {string} category - Category name from URL
 * @returns {string} Normalized category name
 */
export const normalizeCategoryFromURL = (category) => {
  if (!category) return '';
  
  // Handle the specific case mentioned in the issue
  // where "Home+&+Kitchen" should become "Home & Kitchen"
  let normalized = safeDecodeURIComponent(category);
  
  // Additional cleanup for edge cases
  normalized = normalized
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim(); // Remove leading/trailing whitespace
  
  return normalized;
};

/**
 * Sanitize and fix malformed URLs from external sources (like Pinterest)
 * @param {string} url - The potentially malformed URL
 * @returns {string} Sanitized URL
 */
export const sanitizeExternalURL = (url) => {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams();
    
    // Handle malformed category parameters from Pinterest
    urlObj.searchParams.forEach((value, key) => {
      // Fix Pinterest's mangling of "Home+&+Kitchen" type parameters
      if (key === 'category' && value.includes('+') && urlObj.search.includes('+Kitchen')) {
        // Reconstruct the category from the malformed URL
        const searchString = urlObj.search;
        const categoryMatch = searchString.match(/category=([^&]*)/);
        if (categoryMatch) {
          const rawCategory = decodeURIComponent(categoryMatch[1]);
          // If it looks like a partial category, try to reconstruct
          if (rawCategory.endsWith('+') && searchString.includes('+Kitchen')) {
            const reconstructed = rawCategory.replace(/\+$/, ' & Kitchen');
            params.set('category', reconstructed);
            return;
          }
        }
      }
      
      // Handle other malformed parameters
      if (key.startsWith('+') || key.includes('+')) {
        // Skip malformed parameter names
        return;
      }
      
      params.set(key, value);
    });
    
    urlObj.search = params.toString();
    return urlObj.toString();
  } catch (error) {
    console.warn('Failed to sanitize URL:', url, error);
    return url;
  }
};

/**
 * Parse URL parameters with defensive handling for malformed URLs
 * @param {string} searchString - URL search string (e.g., "?category=Home+&+Kitchen")
 * @returns {URLSearchParams} Sanitized URLSearchParams
 */
export const parseURLWithDefense = (searchString) => {
  if (!searchString) return new URLSearchParams();
  
  try {
    // Check for Pinterest-style malformation patterns BEFORE parsing
    if (searchString.includes('+&+Kitchen') || searchString.includes('+&+Body')) {
      let cleanSearch = searchString;
      
      // Fix specific Pinterest malformation patterns with direct string replacement
      if (cleanSearch.includes('Home+&+Kitchen')) {
        cleanSearch = cleanSearch.replace('Home+&+Kitchen', 'Home%20%26%20Kitchen');
      }
      if (cleanSearch.includes('Bath+&+Body')) {
        cleanSearch = cleanSearch.replace('Bath+&+Body', 'Bath%20%26%20Body');
      }
      
      // Handle other potential category + Kitchen/Body patterns
      cleanSearch = cleanSearch.replace(/([^&=]+)\+&\+Kitchen/g, '$1%20%26%20Kitchen');
      cleanSearch = cleanSearch.replace(/([^&=]+)\+&\+Body/g, '$1%20%26%20Body');
      
      return new URLSearchParams(cleanSearch);
    }
    
    // Normal parsing for non-malformed URLs
    return new URLSearchParams(searchString);
  } catch (error) {
    console.warn('Failed to parse URL parameters:', searchString, error);
    return new URLSearchParams();
  }
};

/**
 * Generate a SEO-friendly URL slug from product name
 * @param {string} productName - The product name to convert to URL slug
 * @param {number} maxLength - Maximum length of the resulting slug (default: 50)
 * @returns {string} SEO-friendly URL slug
 */
export const generateProductSlug = (productName, maxLength = 50) => {
  if (!productName) return '';
  
  return productName
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, '-')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, '')
    // Limit length
    .substring(0, maxLength)
    // Remove trailing hyphen if truncated
    .replace(/-$/, '');
};

/**
 * Generate a complete product URL with optional SEO-friendly name
 * @param {string} countryCode - Country code for the URL
 * @param {string} productId - Product ID
 * @param {string} productName - Optional product name for SEO
 * @returns {string} Complete product URL
 */
export const generateProductUrl = (countryCode, productId, productName = null) => {
  const baseUrl = `/country/${countryCode}/products/${productId}`;
  
  if (productName) {
    const slug = generateProductSlug(productName);
    if (slug) {
      return `${baseUrl}/name/${slug}`;
    }
  }
  
  return baseUrl;
};

/**
 * Parse product information from URL parameters
 * Supports both old format (/products/:productId) and new format (/products/:productId/name/:productName)
 * @param {object} params - URL parameters from React Router
 * @returns {object} Object with productId and productName (if available)
 */
export const parseProductParams = (params) => {
  const { productId, productName } = params;
  
  return {
    productId,
    productName: productName ? productName.replace(/-/g, ' ') : null
  };
};

export default {
  safeDecodeURIComponent,
  safeEncodeURIComponent,
  getSearchParam,
  setSearchParam,
  buildQueryString,
  parseQueryString,
  normalizeCategoryFromURL,
  sanitizeExternalURL,
  parseURLWithDefense,
  generateProductSlug,
  generateProductUrl,
  parseProductParams
};
